/** @jsx jsx */
import { css, jsx } from '@compiled/react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	FloatingToolbarContainer,
	FloatingToolbarContent,
	FloatingToolbarSeparator,
	FloatingToolbarSideContent,
} from '../../common/ui/index.tsx';
import messages from '../messages.tsx';

type SkeletonBaseProps = {
	width?: string;
};

const SkeletonBase = (props: SkeletonBaseProps) => (
	<Skeleton
		{...props}
		height="7px"
		isShimmering
		color={token('color.background.accent.gray.subtle')}
		ShimmeringEndColor={token('color.background.accent.gray.bolder')}
		width={props.width || '100%'}
		aria-hidden="true"
	/>
);

export type SkeletonActionButtonProps = {
	width?: string;
	text?: string;
	hasIcon?: boolean;
};

const SkeletonActionButton = ({ width, text, hasIcon }: SkeletonActionButtonProps) => {
	return (
		<Box xcss={skeletonButtonStyles} aria-hidden="true" testId="floating-toolbar.ui.skeleton.box">
			<SkeletonBase width={width} />
			<Inline>
				<Box xcss={invisibleTextStyles}>{text}</Box>
				<Box xcss={hasIcon ? iconSpacerStyles : noIconSpacerStyles} />
			</Inline>
		</Box>
	);
};

const SkeletonCloseIcon = () => (
	<Box xcss={closeIconContainerStyles}>
		<SkeletonBase width="16px" />
	</Box>
);

// These props are used to calculate a more appropriate skeleton
export type SkeletonToolbarProps = {
	hasSelectAllButton: boolean;
	buttons: SkeletonActionButtonProps[];
};

export const ToolbarSkeleton = ({
	hasSelectAllButton,
	buttons,
}: SkeletonToolbarProps): JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<FloatingToolbarContainer>
			<FloatingToolbarContent>
				<FloatingToolbarSideContent>
					<Box>
						<SkeletonBase />
						<Inline space="space.050" aria-hidden="true">
							{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
							<h2 css={selectedContainer}>
								<FormattedMessage
									{...messages.selectedItemsLabel}
									values={{
										numberOfSelectedItems: 1,
										numberSelected: <Box xcss={numberSelectedStyles}>{1}</Box>,
									}}
								/>
							</h2>
						</Inline>
					</Box>
				</FloatingToolbarSideContent>
				{hasSelectAllButton && (
					<SkeletonActionButton text={formatMessage(messages.selectAllButtonLabel)} hasIcon />
				)}
				<FloatingToolbarSeparator />
				<Inline alignBlock="center">
					{buttons.map((button) => {
						return <SkeletonActionButton key={button.text} {...button} />;
					})}
					<SkeletonCloseIcon />
				</Inline>
			</FloatingToolbarContent>
		</FloatingToolbarContainer>
	);
};

const skeletonButtonStyles = xcss({
	paddingInlineStart: 'space.100',
	paddingInlineEnd: 'space.150',
});

const closeIconContainerStyles = xcss({
	paddingInlineStart: 'space.100',
	paddingInlineEnd: 'space.100',
});

const selectedContainer = css({
	font: token('font.body'),
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	alignContent: 'center',
	visibility: 'hidden',
	height: '0',
});

const iconSpacerStyles = xcss({ width: '26px' });

const noIconSpacerStyles = xcss({ width: '4px' });

const invisibleTextStyles = xcss({ height: '0', visibility: 'hidden' });

const numberSelectedStyles = xcss({ width: '24px', marginRight: 'space.050' });
